import useUpdateRecordNameTemplate from "api/api-hooks/modules/useUpdateRecordNameTemplate";
import AppPageHeader from "components/Headers/AppPageHeader";
import AppBadge from "elements/AppBadge";
import EditSaveButton from "elements/EditSaveButton";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Card, CardBody, Badge } from "reactstrap";
import { Tooltip, Whisper } from "rsuite";
import { newToastEvent } from "utils/ToastUtil";
import DisplayName from "../../Inputs/DisplayName";
import RecordNameImage from "./RecordNameImage";
import RecordNameMetadata from "./RecordNameMetadata";

/**
 * Record Name, Image, Metadata configuration component.
 *
 * customSave allows for a custom save function to be passed.
 * customSave is currently used by CreateModuleFromCSV drawer.
 */
const RecordNameTemplate = ({ currentModule, customSave }) => {
  const [recordDetailPageTitle, setRecordDetailPageTitle] = useState(
    customSave ? "" : "Loading..."
  );
  const [isEditing, setIsEditing] = useState(!!customSave);
  const {
    updateRecordNameTemplate,
    updatingRecordNameTemplate,
    updateRecordNameTemplateError,
  } = useUpdateRecordNameTemplate();

  const handleSave = () => {
    updateRecordNameTemplate({
      moduleAPIName: currentModule.APIName,
      recordNameTemplate: recordDetailPageTitle,
      onSuccess: () => {
        setIsEditing(false);
        newToastEvent("success", "Successfully saved Record Name Template");
      },
    });
  };

  const replaceVariable = (item) => {
    let out = [];

    if (!item?.includes("{{") && !item?.includes("}}")) {
      return item;
    }

    for (let i = 0; i < currentModule?.Fields?.length; i++) {
      if (item?.includes(`{{${currentModule?.Fields[i]?.APIName}}}`)) {
        let remainderArr = item?.split(
          `{{${currentModule?.Fields[i]?.APIName}}}`
        );

        out.push(
          <div className="d-inline-block text-dark">
            {!remainderArr[0]?.includes("{{") &&
            !remainderArr[0]?.includes("}}")
              ? remainderArr[0]
              : null}
            <Whisper
              trigger="hover"
              // delayOpen={400}
              placement="auto"
              speaker={
                <Tooltip>
                  <p className="mb-0" style={{ fontSize: 13 }}>
                    Dynamically includes the{" "}
                    <span className="font-weight-bold">
                      {currentModule?.Fields[i]?.Name}{" "}
                    </span>
                    field.
                  </p>
                </Tooltip>
              }
            >
              <div className="cursor-pointer">
                <AppBadge>{currentModule?.Fields[i]?.Name}</AppBadge>
              </div>
            </Whisper>
            {!remainderArr[remainderArr?.length - 1]?.includes("{{") &&
            !remainderArr[remainderArr?.length - 1]?.includes("}}")
              ? remainderArr[remainderArr?.length - 1]
              : null}
          </div>
        );
      }
    }

    return (
      <>
        {out.map((i, index) => (
          <Fragment key={index}>{i}</Fragment>
        ))}
      </>
    );
  };

  const formattedTitle = useMemo(() => {
    if (!recordDetailPageTitle && currentModule) {
      return <Badge color="warning">No Formatting Set</Badge>;
    }

    let outArr = recordDetailPageTitle.split(" ");

    return outArr.map((item, index) => (
      <Fragment key={index}>{replaceVariable(item)}</Fragment>
    ));
  }, [recordDetailPageTitle]);

  useEffect(() => {
    if (!!customSave) return;

    let pageTitle = currentModule?.Templates?.RecordDetailView?.PageTitle
      ? currentModule.Templates.RecordDetailView.PageTitle
      : "";

    setRecordDetailPageTitle(pageTitle);
  }, [customSave, currentModule]);

  const openEditPageName = (open) => {
    let module = currentModule;

    if (!module) {
      return;
    }

    if (!open) {
      let pageTitle = module.Templates.RecordDetailView.PageTitle
        ? module.Templates.RecordDetailView.PageTitle
        : "";

      setRecordDetailPageTitle(pageTitle);
      setIsEditing(false);

      return;
    }

    setIsEditing(true);
  };

  return (
    <>
      <div className="py-3 mb-3 border-bottom border-lighter">
        <AppPageHeader
          size="sm"
          title={"Record Name Format"}
          description={`Use fields to customize how the name of records in this data source are formatted.`}
          titleBreakpoints={{ xs: 6, sm: 9, md: 10, lg: "auto" }}
          actionsBreakpoints={{ xs: 6, sm: 3, md: 2, lg: "auto" }}
          actionClass="d-flex justify-content-end"
          customContent={
            !customSave && (
              <EditSaveButton
                isEditing={isEditing}
                setIsEditing={openEditPageName}
                onSave={handleSave}
                isSubmitting={updatingRecordNameTemplate}
              />
            )
          }
        />
      </div>
      {isEditing ? (
        <DisplayName
          existingValue={recordDetailPageTitle}
          onChange={(val) => {
            customSave && customSave({ recordName: val.newValue });
            setRecordDetailPageTitle(val.newValue);
          }}
          fields={currentModule ? currentModule.Fields : []}
        />
      ) : (
        <Card className="border">
          <CardBody className="p-2">{formattedTitle}</CardBody>
        </Card>
      )}
      <RecordNameImage module={currentModule} customSave={customSave} />
      <RecordNameMetadata module={currentModule} customSave={customSave} />
    </>
  );
};

export default RecordNameTemplate;
